import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function PartnerRegister() {
  const { registerPartner, user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", phone: "", email: "", password: "", accept_terms: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(user.role === "partner" ? "/partner" : "/", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.accept_terms) return setError("You must accept the referral terms.");
    setLoading(true);
    try { await registerPartner(f); navigate("/partner", { replace: true }); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const fld = "bg-white/5 border-white/10 text-white";
  return (
    <AuthShell title="Become a Partner" subtitle="Register to start referring businesses and earning commission."
      footer={<>Already a partner? <Link to="/login?role=partner" className="text-cyan-300 hover:underline">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="referral-signup-form">
        {error && <div data-testid="register-error" className="text-sm text-red-300 bg-red-500/10 rounded-lg px-3 py-2">{error}</div>}
        <div><Label className="text-slate-200 mb-1.5 block">Full name</Label><Input data-testid="register-name" required className={fld} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label className="text-slate-200 mb-1.5 block">Phone</Label><Input data-testid="register-phone" required className={fld} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div><Label className="text-slate-200 mb-1.5 block">Email</Label><Input data-testid="register-email" type="email" required className={fld} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div><Label className="text-slate-200 mb-1.5 block">Password</Label><Input data-testid="register-password" type="password" required minLength={6} className={fld} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
        <label className="flex items-center gap-2.5 text-sm text-slate-300"><Checkbox data-testid="register-terms" checked={f.accept_terms} onCheckedChange={(c) => setF({ ...f, accept_terms: !!c })} /> I accept the <Link to="/legal/referral-terms" className="text-cyan-300 hover:underline">referral terms</Link>.</label>
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full h-11 bg-violet-600 hover:bg-violet-500 text-white font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create partner account"}
        </Button>
      </form>
    </AuthShell>
  );
}
